import { RefObject, useEffect, useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import { AnyFormApi, useStore } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  isApiError,
  extractFieldErrors,
  getGeneralErrors,
} from '@/api/REST/errors';
import type { ApiError } from '@/api/REST/errors';
import { SaveButton, type SaveButtonProps } from '@/components/ui/SaveButton';

interface FormActionsProps {
  form: AnyFormApi;
  formRef: RefObject<HTMLFormElement | null>;
  'data-automation-id': string; // when used for Posthog action setups, this needs to be unique throughout the app to avoid conflicts, e.g. "edit-expense-form-actions"
  onSubmit: () => Promise<void>;
  onSubmitAndNew?: () => Promise<void>;
  onReset?: () => void;
  onCancel?: () => void;
  isSubmitDisabled?: boolean;
  isResetDisabled?: boolean;
  cancelNavigateToUrl?: string | (() => string);
  successNavigationUrl?: string | (() => string);
  getErrorMessageFromError?: (error: ApiError) => string | null;
  submitButtonProps?: Partial<SaveButtonProps>;
}

export const SaveFormActions = ({
  form,
  formRef,
  onSubmit,
  onSubmitAndNew,
  onReset,
  onCancel,
  isSubmitDisabled = false,
  isResetDisabled = false,
  cancelNavigateToUrl,
  successNavigationUrl,
  getErrorMessageFromError,
  submitButtonProps = {},
  'data-automation-id': dataAutomationId,
}: FormActionsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('forms');
  const { t: tErrors, i18n: i18nErrors } = useTranslation('errors');

  const [loading, setLoading] = useState(false);
  const [lastClickedAction, setLastClickedAction] = useState<
    'submit' | 'submitAndNew' | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  if (!form) {
    // log error, but don't return early to not violate rules of hooks
    console.error('Form instance is required for SaveFormActions');
  }

  if (!formRef) {
    // log error, but don't return early to not violate rules of hooks
    console.error('Form ref is required for SaveFormActions');
  }

  const isSubmitSuccessful = useStore(
    form?.store,
    (state) => state.isSubmitSuccessful,
  );

  // we still rely on error throwing to utilize try/catch in submission handlers, this is just to log errors for debugging
  const formErrorList = useStore(form?.store, (state) => state.errors);

  const handleCancelClick = () => {
    //   if (location.key !== 'default') {
    //     navigate(-1);
    //   } else {
    //     navigate(cancelFallbackUrl, { replace: true });
    //   }
    if (onCancel) {
      onCancel();
    }

    const url =
      typeof cancelNavigateToUrl === 'function'
        ? cancelNavigateToUrl()
        : cancelNavigateToUrl;

    if (!url) return;

    navigate(url);
  };

  const resetStates = () => {
    setLoading(false);
    setError(null);
  };

  const handleErrorDisplaying = (err: Error | unknown) => {
    if ((err as Record<string, unknown>)?._handledByToast) return;
    console.error('Form submission error:', err);
    if ((err as Error)?.message === 'Form is invalid') {
      if (formRef.current) {
        // Check if current form has error helper text elements on any fields
        const errorElements = formRef.current.querySelectorAll(
          '.MuiFormHelperText-root.Mui-error',
        );
        if (errorElements.length > 0) {
          return; // Do not display additional error message when there are error messages on the fields
        } else {
          console.error(
            'Form is invalid but no visible error messages found on fields.',
            formErrorList,
          );
          // set generic user facing error message
          setError(t('SubmitErrorFallback'));
          return;
        }
      }
    }

    if (isApiError(err)) {
      const translationOptions = {
        t: tErrors,
        i18n: i18nErrors,
        namespace: 'errors',
      };

      // Set field-level errors directly on the form (convention: backend PascalCase 'FieldName' → form camelCase 'fieldName')
      const fieldErrors = extractFieldErrors(err, translationOptions);
      let hasMatchedFieldErrors = false;
      const unmatchedMessages: string[] = [];

      Object.entries(fieldErrors).forEach(([field, messages]) => {
        const formFieldName = field.charAt(0).toLowerCase() + field.slice(1);
        if (form.getFieldMeta(formFieldName) !== undefined) {
          hasMatchedFieldErrors = true;
          form.setFieldMeta(formFieldName, (prev) => ({
            ...prev,
            errorMap: { ...prev.errorMap, onSubmit: { message: messages[0] } },
          }));
        } else {
          unmatchedMessages.push(messages[0]);
        }
      });

      // Show form-level message for special cases or when no field errors were handled
      const specialMessage = getErrorMessageFromError?.(err);
      if (specialMessage) {
        setError(specialMessage);
      } else if (unmatchedMessages.length > 0) {
        setError(unmatchedMessages[0]);
      } else if (!hasMatchedFieldErrors) {
        const generalErrors = getGeneralErrors(err, translationOptions);
        if (generalErrors.length > 0) {
          setError(generalErrors[0]);
        }
      }
      return;
    }

    setError((err as Error)?.message ?? t('SubmitErrorFallback'));
  };

  const handleOnSubmit = () => {
    resetStates();
    setLastClickedAction('submit');
    setLoading(true);

    /*
     * Trigger onBlur on any focused field so linked-field recalculations run
     * before the form values are read in onSubmit (e.g. Enter-key submission).
     *
     * Use requestAnimationFrame so async onBlur handlers (hook based formatting/ calculations) run before submit
     */
    (document.activeElement as HTMLElement)?.blur();
    requestAnimationFrame(async () => {
      try {
        await onSubmit();
      } catch (err) {
        handleErrorDisplaying(err);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleOnSubmitAndNew = () => {
    resetStates();
    setLastClickedAction('submitAndNew');
    setLoading(true);

    /*
     * Trigger onBlur on any focused field so linked-field recalculations run
     * before the form values are read in onSubmit (e.g. Enter-key submission).
     *
     * Use requestAnimationFrame so async onBlur handlers (hook based formatting/ calculations) run before submit
     */
    (document.activeElement as HTMLElement)?.blur();
    requestAnimationFrame(async () => {
      try {
        if (onSubmitAndNew) {
          await onSubmitAndNew();

          // For "Save and New", we don't navigate away - stay on the form
        }
      } catch (err) {
        handleErrorDisplaying(err);
      } finally {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    if (!isSubmitSuccessful || lastClickedAction !== 'submit') return;

    const url =
      typeof successNavigationUrl === 'function'
        ? successNavigationUrl()
        : successNavigationUrl;

    if (!url) return;

    const timer = setTimeout(() => navigate(url), 1000);
    return () => clearTimeout(timer);
  }, [isSubmitSuccessful]);

  return (
    <Stack
      direction="row"
      gap={1}
      justifyContent="flex-end"
      alignItems="center"
      data-automation-id={dataAutomationId}
    >
      {error && (
        <Typography color={scssVariables.ColorCoral80} variant="label">
          {t('SubmitErrorLabel')}: {error}
        </Typography>
      )}

      {(!!cancelNavigateToUrl || !!onCancel) && (
        <Button
          variant="text"
          color="secondary"
          disabled={loading}
          onClick={handleCancelClick}
          data-automation-id={`${dataAutomationId}-cancel-button`}
        >
          {t('actions.Cancel')}
        </Button>
      )}

      {!!onReset && (
        <Button
          variant="text"
          color="secondary"
          disabled={loading || isResetDisabled}
          onClick={onReset}
          data-automation-id={`${dataAutomationId}-reset-button`}
        >
          {t('actions.Reset')}
        </Button>
      )}

      {!!onSubmitAndNew && (
        <SaveButton
          variant="outlined"
          color="secondary"
          loading={loading && lastClickedAction === 'submitAndNew'}
          isSuccess={isSubmitSuccessful && lastClickedAction === 'submitAndNew'}
          label={t('actions.SaveAndNew')}
          disabled={loading || isSubmitDisabled}
          onClick={handleOnSubmitAndNew}
          data-automation-id={`${dataAutomationId}-submit-and-new-button`}
        />
      )}

      <SaveButton
        type="submit"
        variant="contained"
        color="secondary"
        loading={loading && lastClickedAction === 'submit'}
        isSuccess={isSubmitSuccessful && lastClickedAction === 'submit'}
        disabled={loading || isSubmitDisabled}
        onClick={handleOnSubmit}
        data-automation-id={`${dataAutomationId}-submit-button`}
        {...submitButtonProps}
      />
    </Stack>
  );
};
